/**
 * Cliente HTTP mínimo — fetch nativo + Basic Auth via sessionStorage.
 *
 * Fluxo:
 * 1. Se `cortex_auth` está em sessionStorage, usa direto.
 * 2. Se não está, as chamadas vão sem header — em dev local (sem CORTEX_BETA_USER
 *    na API), passa. Em prod, a API retorna 401 e o App mostra o LoginModal.
 * 3. Em 401, limpamos a creds e o caller decide o que fazer (LoginModal reabre).
 *
 * Escopo: sessionStorage (não localStorage) — ao fechar a aba, re-login.
 * Beta, não é banco, sem guardar senha.
 */
import type {
  Recebimentos6duResponse,
  Tracker6duResponse,
  Calendario6duItem,
  CurvaForwardRow,
  CurvaPreResponse,
  DespesasV2Response,
  DreV2Response,
  ExposicaoResponse,
  FluxoComposicaoResponse,
  FluxoListResponse,
  CalendarioCaixaResponse,
  CfBookDetalheResponse,
  ContasReceberResponse,
  CurvaAbcResponse,
  CurvaPnlResponse,
  CurvaPnlSerieResponse,
  DeltaAnualResponse,
  Dv01IpcaResponse,
  FluxoV2Response,
  PrevisaoVencidaResponse,
  IpcaResponse,
  LancamentoPage,
  LastSyncResponse,
  LiquidacoesResponse,
  MeResponse,
  PldAnualResponse,
  PldDecompInput,
  PldDecompResponse,
  PldHistoricoResponse,
  PldHistoricoDecompResponse,
  PldMensalResponse,
  PldOverviewResponse,
  PldProjecaoLogResponse,
  PldProjecaoResponse,
  PldSemanasResponse,
  PosicaoAbertaMtm6mResponse,
  PosicaoDecompostaResponse,
  PosicaoAbertaResponse,
  Proximo6duResponse,
  RhCustoResponse,
  RhEvolucaoResponse,
  RhHeadcountResponse,
  SaldoV2Response,
  SimuladorV2Request,
  SimuladorV2Response,
  ValuationVarejoResponse,
  VarejoResumoResponse,
  ZeusIntegracaoStatusResponse,
} from "./types";

const SS_KEY = "cortex_auth";

export class AuthRequiredError extends Error {
  constructor() {
    super("auth required");
    this.name = "AuthRequiredError";
  }
}

export function setAuth(user: string, pass: string): void {
  sessionStorage.setItem(SS_KEY, btoa(`${user}:${pass}`));
}

export function clearAuth(): void {
  sessionStorage.removeItem(SS_KEY);
}

export function hasAuth(): boolean {
  return sessionStorage.getItem(SS_KEY) !== null;
}

async function apiGet<T>(
  path: string,
  params?: Record<string, string | string[] | undefined>,
  /** Override do token Basic (já em base64). Usado pelo login p/ VALIDAR uma
   * credencial antes de persistir (#246): sem isso o apiGet leria a sessão
   * vigente, não a que está sendo testada. Omitido = lê a sessão. */
  authTokenOverride?: string,
): Promise<T> {
  const url = new URL(path, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) continue;
      if (Array.isArray(v)) v.forEach((vi) => url.searchParams.append(k, vi));
      else url.searchParams.set(k, v);
    }
  }

  const headers: Record<string, string> = { Accept: "application/json" };
  const auth = authTokenOverride ?? sessionStorage.getItem(SS_KEY);
  if (auth) headers.Authorization = `Basic ${auth}`;

  const res = await fetch(url.pathname + url.search, { headers });
  if (res.status === 401) {
    clearAuth();
    throw new AuthRequiredError();
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<T>;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  const auth = sessionStorage.getItem(SS_KEY);
  if (auth) headers.Authorization = `Basic ${auth}`;

  const res = await fetch(path, { method: "POST", headers, body: JSON.stringify(body) });
  if (res.status === 401) {
    clearAuth();
    throw new AuthRequiredError();
  }
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  return res.json() as Promise<T>;
}

/** `authToken` (Basic base64) valida uma credencial específica sem ler/alterar
 * a sessão — o login usa isso pra checar user:pass antes de persistir (#246). */
export function getMe(authToken?: string): Promise<MeResponse> {
  return apiGet<MeResponse>("/api/me", undefined, authToken);
}

/** DRE Gerencial real (#121e backend, Task #124). Fonte única de DRE no
 * frontend desde a remoção do /api/dre legacy (#131). */
export function getDreV2(opts: {
  regime: string;
  periodo: string;
  mes_ref?: string;
  dt_ini?: string;
  dt_fim?: string;
  comparar?: string;
}): Promise<DreV2Response> {
  return apiGet<DreV2Response>("/api/v2/dre", {
    regime: opts.regime,
    periodo: opts.periodo,
    mes_ref: opts.mes_ref,
    dt_ini: opts.dt_ini,
    dt_fim: opts.dt_fim,
    comparar: opts.comparar,
  });
}

/** Task #124: lançamentos detalhados por linha DRE (#121e backend). */
export function getDespesasV2(opts: {
  linha_dre_codigo: string;
  regime: string;
  periodo: string;
  mes_ref?: string;
  dt_ini?: string;
  dt_fim?: string;
  limit?: number;
}): Promise<DespesasV2Response> {
  return apiGet<DespesasV2Response>("/api/v2/despesas", {
    linha_dre_codigo: opts.linha_dre_codigo,
    regime: opts.regime,
    periodo: opts.periodo,
    mes_ref: opts.mes_ref,
    dt_ini: opts.dt_ini,
    dt_fim: opts.dt_fim,
    limit: opts.limit?.toString(),
  });
}

/** #60: /api/fluxo virou envelope {rows, as_of} — o as-of e do DADO servido
 * (snapshot do precompute ou live), nunca do relogio do browser. */
export function getFluxo(
  data_inicio: string,
  data_fim: string,
  business_unit?: string[],
): Promise<FluxoListResponse> {
  return apiGet<FluxoListResponse>("/api/fluxo", {
    data_inicio,
    data_fim,
    business_unit,
  });
}

/** Task #132: saldo bancario real (Omie) — substitui mock saldoAtualTotal
 * no card "Saldo atual em conta". Endpoint coexiste com /api/saldo legacy. */
export function getSaldoV2(): Promise<SaldoV2Response> {
  return apiGet<SaldoV2Response>("/api/v2/saldo");
}

/** Task #132: realizado (Omie) + projetado (Zeus) do mes — alimenta os
 * cards "Entradas no mes" / "Saidas no mes" (substitui projecao Zeus
 * rotulada como realizada). `dias` (30/60/90) escala a janela das series
 * diarias do grafico (toggle); omitido = 30 (default do backend). */
export function getFluxoV2(hoje?: string, dias?: number): Promise<FluxoV2Response> {
  return apiGet<FluxoV2Response>("/api/v2/fluxo", {
    hoje,
    dias: dias?.toString(),
  });
}

/** #149/#154: titulos pendentes com previsao de pagamento JA passada (fora de
 * qualquer barra do grafico). Bloco proprio na tela do Fluxo. */
export function getPrevisaoVencida(hoje?: string): Promise<PrevisaoVencidaResponse> {
  return apiGet<PrevisaoVencidaResponse>("/api/v2/fluxo/previsao-vencida", { hoje });
}

/** #30b: lançamentos que compõem o Fluxo da janela (abas de detalhe do
 * "Baixar Excel"). Mesma base das séries do /api/v2/fluxo (sem re-derivar). */
export function getFluxoComposicao(opts: {
  dias: number;
  hoje?: string;
}): Promise<FluxoComposicaoResponse> {
  return apiGet<FluxoComposicaoResponse>("/api/fluxo/composicao", {
    dias: String(opts.dias),
    hoje: opts.hoje,
  });
}

export function getLiquidacoes(opts?: {
  data_alvo?: string;
  inclui_proximos_2du?: boolean;
}): Promise<LiquidacoesResponse> {
  // expand_ids removido pos-#54: granularidade ja eh per-trade.
  return apiGet<LiquidacoesResponse>("/api/fluxo/liquidacoes", {
    data_alvo: opts?.data_alvo,
    inclui_proximos_2du: opts?.inclui_proximos_2du ? "true" : undefined,
  });
}

/** #244: `ancora` (6du navegado, YYYY-MM-DD) faz a tabela acompanhar o card
 * 6DU. Omitido = ciclo corrente (contém hoje). `hoje` segue p/ simulação. */
export function getCalendarioCaixa(
  hoje?: string,
  ancora?: string,
): Promise<CalendarioCaixaResponse> {
  return apiGet<CalendarioCaixaResponse>("/api/fluxo/calendario-caixa", { hoje, ancora });
}

export function getCurvaPnl(): Promise<CurvaPnlResponse> {
  return apiGet<CurvaPnlResponse>("/api/curva-pnl");
}

/** #244: detalhe POR CLIENTE do CF por Book (#64) — ferramenta de conferência
 * da Mesa. Mesma classificação/base do card; reconcilia por construção. */
export function getCfBookDetalhe(hoje?: string): Promise<CfBookDetalheResponse> {
  return apiGet<CfBookDetalheResponse>("/api/curva-pnl/detalhe-book", { hoje });
}

/** #29: DV01 do IPCA — sensibilidade da valuation indexada à implícita #218
 * (header DV01 Total/Next + tabela por ano). Compute live, fora do hot-path. */
export function getDv01Ipca(hoje?: string): Promise<Dv01IpcaResponse> {
  return apiGet<Dv01IpcaResponse>("/api/curva-pnl/dv01-ipca", { hoje });
}

/** #251: série diária do resultado do Book Brasil (sparkline de tendência). */
export function getCurvaPnlSerie(dias = 30): Promise<CurvaPnlSerieResponse> {
  return apiGet<CurvaPnlSerieResponse>("/api/curva-pnl/serie", { dias: String(dias) });
}

/** Δ Anual MtM (energia) — delta vs foto 31/12 (VF + VP + por_ano + metadados).
 * Acende a linha "Δ Anual" do card Resultado (substitui o baseline 31/12 que
 * nunca entrou — #67). MtM vivo do aberto + realizado do ano embutido. */
export function getPnlDeltaAnual(): Promise<DeltaAnualResponse> {
  return apiGet<DeltaAnualResponse>("/api/pnl/delta-anual");
}

// --- Curva Pré (DI) + IPCA (#218; aba Juros & Inflação #220) ----------------

/** Curva pré as-of. `dataRef` YYYY-MM-DD (string SEMPRE — nunca Date, evita
 * timezone); omitido = mais recente. 404 (data não materializada) → null —
 * estado de domínio do snapshot diário, não exceção. */
export async function getCurvaPre(dataRef?: string): Promise<CurvaPreResponse | null> {
  try {
    return await apiGet<CurvaPreResponse>("/api/curva-pre", { data_ref: dataRef });
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("HTTP 404")) return null;
    throw err;
  }
}

/** IPCA — realizado + implícita as-of. `dataRef` YYYY-MM-DD (string sempre);
 * omitido = snapshot mais recente. 404 (as-of não materializado) → null,
 * mesmo contrato do getCurvaPre (#228 item 2). */
export async function getIpca(dataRef?: string): Promise<IpcaResponse | null> {
  try {
    return await apiGet<IpcaResponse>("/api/ipca", { data_ref: dataRef });
  } catch (err) {
    if (err instanceof Error && err.message.startsWith("HTTP 404")) return null;
    throw err;
  }
}

export function getLastSync(): Promise<LastSyncResponse> {
  return apiGet<LastSyncResponse>("/api/zeus/last-sync");
}

// --- Controle PLD (Task #190 Fase 2) --------------------------------------
// Endpoints por submercado; as visões de 4 séries combinam chamadas no front.

export function getPldOverview(
  mes_ref: string,
  submercado: string,
): Promise<PldOverviewResponse> {
  return apiGet<PldOverviewResponse>("/api/v2/pld/overview", { mes_ref, submercado });
}

export function getPldSemanasOperativas(
  mes_ref: string,
  submercado: string,
): Promise<PldSemanasResponse> {
  return apiGet<PldSemanasResponse>("/api/v2/pld/semanas-operativas", {
    mes_ref,
    submercado,
  });
}

export function getPldHistorico(
  meses: number,
  submercado: string,
): Promise<PldHistoricoResponse> {
  return apiGet<PldHistoricoResponse>("/api/v2/pld/historico", {
    meses: meses.toString(),
    submercado,
  });
}

export function getPldProjecao(
  mes_ref: string,
  submercado: string,
): Promise<PldProjecaoResponse> {
  return apiGet<PldProjecaoResponse>("/api/v2/pld/projecao", { mes_ref, submercado });
}

export function getPldMensal(submercado: string): Promise<PldMensalResponse> {
  return apiGet<PldMensalResponse>("/api/v2/pld/mensal", { submercado });
}

// #29 PR3: histórico Realizado × DECOMP × Δ por submercado (mensal + anual).
export function getPldHistoricoDecomp(
  submercado: string,
): Promise<PldHistoricoDecompResponse> {
  return apiGet<PldHistoricoDecompResponse>("/api/v2/pld/historico-decomp", { submercado });
}

// #26: log as-of da projeção × realizado (track record do modelo).
export function getPldProjecaoLog(
  submercado: string,
): Promise<PldProjecaoLogResponse> {
  return apiGet<PldProjecaoLogResponse>("/api/v2/pld/projecao-log", { submercado });
}

export function getPldAnual(submercado: string): Promise<PldAnualResponse> {
  return apiGet<PldAnualResponse>("/api/v2/pld/anual", { submercado });
}

export function postPldDecomp(body: PldDecompInput): Promise<PldDecompResponse> {
  return apiPost<PldDecompResponse>("/api/v2/pld/decomp", body);
}

/** Task #87: status detalhado por dataset Zeus pra badge no TopBar. */
export function getZeusIntegracaoStatus(): Promise<ZeusIntegracaoStatusResponse> {
  return apiGet<ZeusIntegracaoStatusResponse>("/api/integracao/zeus/status");
}

export function getPosicaoAberta(opts?: {
  mes_ref_inicial?: string;
  mes_ref_final?: string;
}): Promise<PosicaoAbertaResponse> {
  return apiGet<PosicaoAbertaResponse>("/api/fluxo/posicao-aberta", {
    mes_ref_inicial: opts?.mes_ref_inicial,
    mes_ref_final: opts?.mes_ref_final,
  });
}

export function getPosicaoAbertaMtm6m(opts?: {
  mes_inicio?: string;
  meses?: number;
}): Promise<PosicaoAbertaMtm6mResponse> {
  return apiGet<PosicaoAbertaMtm6mResponse>("/api/fluxo/posicao-aberta-mtm-6m", {
    mes_inicio: opts?.mes_inicio,
    meses: opts?.meses?.toString(),
  });
}

export function getCurvaForward(opts?: {
  mes_ref_inicial?: string;
  mes_ref_final?: string;
  ind_oficial?: boolean;
}): Promise<CurvaForwardRow[]> {
  return apiGet<CurvaForwardRow[]>("/api/curva-forward", {
    mes_ref_inicial: opts?.mes_ref_inicial,
    mes_ref_final: opts?.mes_ref_final,
    ind_oficial:
      opts?.ind_oficial === undefined ? undefined : String(opts.ind_oficial),
  });
}

export function getProximo6du(hoje?: string): Promise<Proximo6duResponse> {
  return apiGet<Proximo6duResponse>("/api/calendario/proximo-6du", { hoje });
}

/** Task #170: janela navegável de 6dus (prev anteriores + próximo + next
 * seguintes). Alimenta a navegação prev/next do Card "Próximo 6DU". */
export function getCalendario6dus(opts?: {
  prev?: number;
  next?: number;
  hoje?: string;
}): Promise<Calendario6duItem[]> {
  return apiGet<Calendario6duItem[]>("/api/calendario/6dus", {
    prev: opts?.prev?.toString(),
    next: opts?.next?.toString(),
    hoje: opts?.hoje,
  });
}
export function getLancamentos(params: {
  data_inicio: string;
  data_fim: string;
  business_unit?: string[];
  tipo?: "receita" | "despesa";
  limite?: number;
  offset?: number;
}): Promise<LancamentoPage> {
  return apiGet<LancamentoPage>("/api/lancamentos", {
    data_inicio: params.data_inicio,
    data_fim: params.data_fim,
    business_unit: params.business_unit,
    tipo: params.tipo,
    limite: params.limite?.toString(),
    offset: params.offset?.toString(),
  });
}

// --- Issue #242: aba Varejo v1 -----------------------------------------------

export function getVarejoResumo(): Promise<VarejoResumoResponse> {
  return apiGet<VarejoResumoResponse>("/api/varejo/resumo");
}

/** Valuation da carteira varejo (réplica do Estudo MTM #4) — snapshot estático,
 * off (nominal/cenário A) + on (IPCA) num só payload (toggle é client-side). */
export function getVarejoValuation(hoje?: string): Promise<ValuationVarejoResponse> {
  return apiGet<ValuationVarejoResponse>("/api/varejo/valuation", { hoje });
}

/** #2 (10/08): valuation da carteira VIVA. spread=0 → snapshot precomputado
 * (as-of no payload); spread≠0 → recálculo LIVE (prêmio de risco é ato do
 * usuário — D3). */
export function getVarejoValuationViva(
  spread?: number,
): Promise<ValuationVarejoResponse> {
  return apiGet<ValuationVarejoResponse>("/api/varejo/valuation", {
    fonte: "viva",
    spread: spread !== undefined && spread !== 0 ? String(spread) : undefined,
  });
}

// --- Issue #34: posição decomposta tipo × submercado -------------------------

/** Matriz (tipo_energia × submercado) por vértice da Curva Curta/Longa —
 * view aditiva sobre as células do motor #57 (global intocado). "Todos" =
 * omitir o filtro. Net exibido fecha EXATO com as células (F-34A);
 * mwh_global/mwm_global reconciliam com a Curva da tela. */
export function getPosicaoDecomposta(opts?: {
  tipo?: string;
  submercado?: string;
}): Promise<PosicaoDecompostaResponse> {
  return apiGet<PosicaoDecompostaResponse>("/api/posicao-decomposta", {
    tipo: opts?.tipo,
    submercado: opts?.submercado,
  });
}

// --- Issue #30 camada A: Exposição MtM por contraparte -----------------------

/** Exposição MtM líquida por contraparte (#30 camada A) — netado + drill-down
 * embutido. Limite/Consumo GATED no engine de crédito (vêm null). */
export function getExposicao(hoje?: string): Promise<ExposicaoResponse> {
  return apiGet<ExposicaoResponse>("/api/exposicao", { hoje });
}

// --- Contas a Receber (Fase 1, Zeus-only; PR #312 backend) -------------------

/** Recebível de energia a vencer (vendas vigentes externas). Total reconcilia
 * com a projeção de entradas do Fluxo. `hoje` = override as-of (debug). */
/** #15: esperados do próximo 6DU por contraparte (default = próximo 6DU CCEE). */
export function getRecebimentos6du(): Promise<Recebimentos6duResponse> {
  return apiGet<Recebimentos6duResponse>("/api/recebimentos-6du");
}

/** #15 joint: esperado × recebido do 6DU por contrato (recebido = Sync F0;
 * degrada honesto com recebido_disponivel=false). */
export function getRecebimentos6duTracker(): Promise<Tracker6duResponse> {
  return apiGet<Tracker6duResponse>("/api/recebimentos-6du/tracker");
}

export function getContasReceber(opts?: {
  hoje?: string;
  top?: number;
}): Promise<ContasReceberResponse> {
  return apiGet<ContasReceberResponse>("/api/contas-receber", {
    hoje: opts?.hoje,
    top: opts?.top?.toString(),
  });
}

export function getVarejoCurvaAbc(params: {
  book: string;
  de?: string;
  ate?: string;
}): Promise<CurvaAbcResponse> {
  return apiGet<CurvaAbcResponse>("/api/varejo/curva-abc", {
    book: params.book,
    de: params.de,
    ate: params.ate,
    detalhe: "mes",
  });
}

// ---- Simulador what-if (#28 PR-3) -------------------------------------------

/** POST /api/simulador v2 (#36+#37) — STATELESS (nada gravado; guard no
 * payload). O front manda SEMPRE `tranches[]` (1 tranche = caso simples);
 * é uma CONSULTA com body estruturado; input incompleto → 400 explícito
 * (o front valida antes e nem chama). */
export function postSimuladorV2(body: SimuladorV2Request): Promise<SimuladorV2Response> {
  return apiPost<SimuladorV2Response>("/api/simulador", body);
}

// ---- RH (#29 F1b) — endpoints agregados (PII fica no backend) ----

export function getRhHeadcount(mes?: string): Promise<RhHeadcountResponse> {
  return apiGet<RhHeadcountResponse>("/api/rh/headcount", { mes });
}

export function getRhCusto(mes?: string): Promise<RhCustoResponse> {
  return apiGet<RhCustoResponse>("/api/rh/custo", { mes });
}

export function getRhEvolucao(): Promise<RhEvolucaoResponse> {
  return apiGet<RhEvolucaoResponse>("/api/rh/evolucao");
}
